import { useEffect, useState } from 'react';
import { localeDirection } from '@drbell/i18n/direction';

/**
 * A line from the DrBell team at the top of the care site and the
 * organisation console — docs/08-control-plane.md, "App announcements".
 *
 * Written in the admin console (Settings → App announcements) and read from
 * `/api/config`, in the reader's language or English. A banner, never a wall:
 * it pushes the page down a line and blocks nothing. Hidden with ×, when the
 * team allows it, and remembered per banner in this browser.
 *
 * The words around it ("Learn more", "Hide") are English like the rest of
 * these two sites; the banner's own words are the reader's.
 */
export interface WebAnnouncement {
  id: string;
  title: string;
  body: string;
  locale: string;
  linkUrl: string | null;
  severity: 'info' | 'warning';
  dismissible: boolean;
  startsAt: number | null;
  endsAt: number | null;
}

const DISMISSED_KEY = 'drbell.announcements.dismissed';
const MAX_REMEMBERED = 50;
const MAX_BANNERS = 2;

function readDismissed(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: string[]) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-MAX_REMEMBERED)));
  } catch {
    /* private window: hidden for this visit only */
  }
}

/** Live now, and not hidden here. The server chose them; the dates are checked again for a tab left open. */
export function visibleWebAnnouncements(list: readonly WebAnnouncement[], dismissed: readonly string[], now: number): WebAnnouncement[] {
  return list
    .filter((a) => a.title.trim() && a.body.trim())
    .filter((a) => (a.startsAt === null || now >= a.startsAt) && (a.endsAt === null || now < a.endsAt))
    .filter((a) => !(a.dismissible && dismissed.includes(a.id)))
    .slice(0, MAX_BANNERS);
}

const httpsOnly = (url: string | null) => (url && /^https:\/\/\S+$/i.test(url) ? url : null);

export function AnnouncementBar({
  surface,
  lang,
  get,
}: {
  surface: 'care' | 'org';
  /** The reader's language; their account's when absent. */
  lang?: string | null;
  /** The site's own API client, with its session cookie. */
  get: <T>(path: string) => Promise<T>;
}) {
  const [items, setItems] = useState<WebAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  useEffect(() => {
    const q = new URLSearchParams({ surface, ...(lang ? { lang } : {}) });
    // A failure is no banner, never an error on the page.
    get<{ announcements?: WebAnnouncement[] }>(`/api/config?${q.toString()}`)
      .then((r) => setItems(r.announcements ?? []))
      .catch(() => setItems([]));
  }, [surface, lang, get]);

  const shown = visibleWebAnnouncements(items, dismissed, Date.now());
  if (shown.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {shown.map((a) => {
        const warning = a.severity === 'warning';
        const link = httpsOnly(a.linkUrl);
        return (
          <div
            key={a.id}
            role={warning ? 'alert' : 'status'}
            lang={a.locale}
            dir={localeDirection(a.locale)}
            className={`flex items-start gap-3 rounded-xl border p-3 text-sm ${
              warning ? 'border-amber-500/50 bg-amber-500/10' : 'border-(--line) bg-(--panel)'
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{a.title}</p>
              <p className="mt-0.5">{a.body}</p>
              {link && (
                <a href={link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-medium text-(--accent) underline">
                  Learn more
                </a>
              )}
            </div>
            {a.dismissible && (
              <button
                type="button"
                onClick={() => {
                  const next = [...dismissed.filter((x) => x !== a.id), a.id];
                  setDismissed(next);
                  writeDismissed(next);
                }}
                aria-label="Hide this notice"
                className="-m-1 flex min-h-9 min-w-9 items-center justify-center rounded-lg text-lg text-(--muted) hover:bg-(--bg)"
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
